'use client';
import React, { useState, useEffect } from 'react';

interface Camera {
  id: string;
  title: string;
  baseUrl: string;
}

const CYRC_CAMERAS: Camera[] = [
  {
    id: 'west-reference',
    title: 'Vue Ouest — référence',
    baseUrl: 'https://metcam.navcanada.ca/dawc_images/wxcam/CYRC/CYRC_W-full-ref-e.jpeg?tstamp=1549985890000',
  },
  {
    id: 'north',
    title: 'Vue Nord',
    baseUrl: 'https://metcam.navcanada.ca/dawc_images/wxcam/CYRC/CYRC_N-full-e.jpeg?tstamp=1781977386000841579',
  },
  {
    id: 'southeast',
    title: 'Vue Sud-Est',
    baseUrl: 'https://metcam.navcanada.ca/dawc_images/wxcam/CYRC/CYRC_SE-full-e.jpeg?tstamp=1781977386000924074',
  },
  {
    id: 'west',
    title: 'Vue Ouest',
    baseUrl: 'https://metcam.navcanada.ca/dawc_images/wxcam/CYRC/CYRC_W-full-e.jpeg?tstamp=1781977386000971322',
  },
];

function getCameraUrlWithRefresh(baseUrl: string, refreshTs: number | null): string {
  if (!refreshTs) return baseUrl;
  return `${baseUrl}&_refresh=${refreshTs}`;
}

function CameraCard({ camera, refreshTs }: { camera: Camera; refreshTs: number | null }) {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const imageUrl = getCameraUrlWithRefresh(camera.baseUrl, refreshTs);

  // Reset state when URL changes (on refresh)
  useEffect(() => {
    setImgError(false);
    setImgLoaded(false);
  }, [imageUrl]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
      {/* Card header */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-800 truncate">{camera.title}</h3>
        <a
          href={camera.baseUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-medium transition-colors duration-150"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          Ouvrir l&apos;image
        </a>
      </div>

      {/* Camera image */}
      <div className="relative bg-slate-900 flex items-center justify-center" style={{ minHeight: '200px' }}>
        {!imgError ? (
          <>
            {!imgLoaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <div className="animate-spin w-7 h-7 border-2 border-blue-400 border-t-transparent rounded-full" />
                <span className="text-xs text-slate-400">Chargement...</span>
              </div>
            )}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={imageUrl}
              alt={`Caméra météo NAV CANADA CYRC — ${camera.title}`}
              className={`w-full object-contain transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
            />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
            <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center">
              <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <line x1="1" y1="1" x2="23" y2="23" strokeWidth="2" />
              </svg>
            </div>
            <p className="text-sm text-slate-300">Image temporairement indisponible</p>
            <a
              href={camera.baseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors duration-150"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Ouvrir l&apos;image
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CamerasContent() {
  const [refreshTs, setRefreshTs] = useState<number | null>(null);

  // Generate refresh timestamp client-side only to avoid hydration mismatch
  useEffect(() => {
    setRefreshTs(Date.now());
  }, []);

  return (
    <div className="h-full page-scroll px-3 py-4 pb-24 space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-lg font-bold text-slate-800">Caméras météo CYRC</h2>
        <p className="text-sm text-slate-500 mt-0.5">Source : NAV CANADA MetCam</p>
      </div>

      {/* Camera grid: 2x2 on desktop, 1 column on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CYRC_CAMERAS.map((camera) => (
          <CameraCard key={camera.id} camera={camera} refreshTs={refreshTs} />
        ))}
      </div>

      {/* Disclaimer */}
      <p className="text-xs text-slate-400 text-center pt-2 pb-2">
        Images NAV CANADA fournies à titre informatif. Toujours valider avec les sources et procédures officielles.
      </p>
    </div>
  );
}